// src/components/ForceUpdateModal.jsx
import React, { useEffect, useRef } from 'react';

const isNativeAndroid = typeof window !== 'undefined' && typeof window.AndroidBridge !== 'undefined';

const btnBase = {
  width: '100%',
  padding: '14px 24px',
  borderRadius: '10px',
  border: 'none',
  color: '#FFFFFF',
  fontSize: '16px',
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'background-color 0.15s ease, transform 0.1s ease',
};

/**
 * Modal bloqueante que aparece quando há uma atualização obrigatória.
 * O usuário NÃO pode fechar este modal - precisa atualizar.
 *
 * Detecta se está dentro do app Android nativo (via AndroidBridge)
 * e oferece botão para abrir a Play Store em vez de apenas recarregar.
 */
export function ForceUpdateModal({ onUpdate, currentVersion, message }) {
  const cardRef = useRef(null);
  const primaryBtnRef = useRef(null);

  useEffect(() => {
    if (!onUpdate) return undefined;
    const previouslyFocused = (typeof document !== 'undefined' && document.activeElement) || null;
    setTimeout(() => primaryBtnRef.current?.focus(), 80);

    const handleKey = (e) => {
      if (e.key !== 'Tab') return;
      const root = cardRef.current;
      if (!root) return;
      const focusables = root.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        try { previouslyFocused.focus(); } catch {}
      }
    };
  }, [onUpdate]);

  if (!onUpdate) return null;

  const handlePlayStore = () => {
    try { window.AndroidBridge.openPlayStore(); } catch { /* fallback */ }
  };

  const defaultMessage = isNativeAndroid
    ? 'Uma nova versão do app está disponível na Play Store. Atualize para continuar.'
    : 'Uma nova versão está disponível. Clique abaixo para atualizar.';

  return (
    <>
      <style>{`
        @keyframes fum-overlay-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes fum-card-in {
          from { opacity: 0; transform: translateY(8px) scale(.985); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @media (prefers-reduced-motion: reduce) {
          .fum-overlay, .fum-card { animation: none !important; }
        }
      `}</style>
    <div
      className="fum-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="fum-title"
      aria-describedby="fum-desc"
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999,
        fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, Inter, Roboto, sans-serif',
        padding: 'max(20px, env(safe-area-inset-top)) max(20px, env(safe-area-inset-right)) max(20px, env(safe-area-inset-bottom)) max(20px, env(safe-area-inset-left))',
        animation: 'fum-overlay-in .25s cubic-bezier(.4,0,.2,1) both',
      }}
    >
      <div
        className="fum-card"
        ref={cardRef}
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '16px',
          padding: '32px',
          maxWidth: '360px',
          width: '100%',
          textAlign: 'center',
          boxShadow: '0 16px 48px rgba(0, 0, 0, 0.18)',
          animation: 'fum-card-in .35s cubic-bezier(.17,.67,.3,1.2) both',
        }}
      >
        <div
          aria-hidden="true"
          style={{
            width: '64px',
            height: '64px',
            backgroundColor: '#E8F5E9',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
            fontSize: '32px',
          }}
        >
          🔄
        </div>

        <h2
          id="fum-title"
          className="deco-headline"
          style={{
            margin: '0 0 16px',
            fontSize: '22px',
            fontWeight: 700,
            color: '#1F2937',
          }}
        >
          Atualização Obrigatória
        </h2>

        <p
          id="fum-desc"
          style={{
            margin: '0 0 24px',
            fontSize: '15px',
            color: '#4B5563',
            lineHeight: 1.5,
          }}
        >
          {message || defaultMessage}
        </p>

        {currentVersion && (
          <p
            style={{
              margin: '0 0 20px',
              fontSize: '12px',
              color: '#9CA3AF',
              fontFamily: 'monospace',
            }}
          >
            Versão atual: {currentVersion}
          </p>
        )}

        {isNativeAndroid ? (
          <>
            <button
              type="button"
              ref={primaryBtnRef}
              onClick={handlePlayStore}
              style={{ ...btnBase, backgroundColor: '#D32F2F', marginBottom: '10px' }}
              onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#B71C1C'; }}
              onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#D32F2F'; }}
              onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.98)'; }}
              onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
            >
              Atualizar na Play Store
            </button>
            <button
              type="button"
              onClick={onUpdate}
              style={{
                ...btnBase,
                backgroundColor: 'transparent',
                color: '#6B7280',
                fontSize: '14px',
                padding: '10px 24px',
              }}
            >
              Recarregar página
            </button>
          </>
        ) : (
          <button
            type="button"
            ref={primaryBtnRef}
            onClick={onUpdate}
            style={{ ...btnBase, backgroundColor: '#D32F2F' }}
            onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#B71C1C'; }}
            onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#D32F2F'; }}
            onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.98)'; }}
            onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
          >
            Atualizar
          </button>
        )}
      </div>
    </div>
    </>
  );
}

export default ForceUpdateModal;
